import React, { useEffect, useState } from 'react';
import { Check, Trash2, Pencil, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

function ResourceEditor({ resource, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(resource.title || '');
  const [url, setUrl] = useState(resource.url || '');
  const [description, setDescription] = useState(resource.description || '');
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <input
        className="form-input"
        placeholder="Title (e.g. First-time buyer guide)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <input
        className="form-input"
        placeholder="Link (https://...)"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <textarea
        className="form-input"
        rows={2}
        placeholder="What is this, and why would your client want it? (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={saving || !title.trim() || !url.trim()}
          onClick={() => onSave({ title: title.trim(), url: url.trim(), description: description.trim() })}
        >
          <Check size={14} /> Save
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function Resources() {
  const { agent } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);

  const loadResources = async () => {
    try {
      const res = await api.get('/agent-resources');
      setResources(res.data.resources || []);
    } catch (e) {
      toast.error('Could not load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadResources(); }, []);

  const createResource = async (payload) => {
    setSavingId('new');
    try {
      const res = await api.post('/agent-resources', payload);
      setResources((prev) => [res.data.resource, ...prev]);
      setAddingNew(false);
      toast.success('Saved — your clients will see this in their portal now');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save resource', { duration: 8000 });
    } finally {
      setSavingId(null);
    }
  };

  const updateResource = async (id, payload) => {
    setSavingId(id);
    try {
      const res = await api.patch(`/agent-resources/${id}`, payload);
      setResources((prev) => prev.map((r) => (r.id === id ? res.data.resource : r)));
      setEditingId(null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update resource', { duration: 8000 });
    } finally {
      setSavingId(null);
    }
  };

  const deleteResource = async (id) => {
    try {
      await api.delete(`/agent-resources/${id}`);
      setResources((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error('Could not delete resource');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Resources</h2>
        <p>
          Links and guides you want every client to have — a mortgage calculator, a first-time-buyer
          checklist, your closing-day walkthrough. Shows up in each client's portal under Resources.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title">Your resources</h3>
          {!addingNew && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAddingNew(true)}>
              Add resource
            </button>
          )}
        </div>

        {addingNew && (
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border, #eee)' }}>
            <ResourceEditor
              resource={{ title: '', url: '', description: '' }}
              saving={savingId === 'new'}
              onSave={createResource}
              onCancel={() => setAddingNew(false)}
            />
          </div>
        )}

        {loading ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>
        ) : resources.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>
            <Link2 size={22} style={{ opacity: 0.5 }} />
            <p style={{ fontSize: 13, marginTop: 8 }}>
              No resources yet. Add a link and every one of {agent?.first_name || 'your'}'s clients will see it
              in their portal.
            </p>
          </div>
        ) : (
          resources.map((r) => (
            <div key={r.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border, #eee)' }}>
              {editingId === r.id ? (
                <ResourceEditor
                  resource={r}
                  saving={savingId === r.id}
                  onSave={(payload) => updateResource(r.id, payload)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{r.title}</div>
                    <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, wordBreak: 'break-all', color: 'var(--accent-light)' }}>
                      {r.url}
                    </a>
                    {r.description && (
                      <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 4, color: 'var(--text-muted)' }}>
                        {r.description}
                      </div>
                    )}
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" title="Edit" onClick={() => setEditingId(r.id)}>
                    <Pencil size={13} />
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" title="Delete" onClick={() => deleteResource(r.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
